import { useCallback, useEffect, useState } from 'react';
import type { InvitationSummary } from 'minka-shared';
import * as adminApi from '@/services/admin-api';
import { describeAdminError } from '@/utils/api-error';

export interface UsePlatformInvitationsResult {
  data: InvitationSummary[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  invite: (email: string) => Promise<void>;
  revoke: (invitationId: string) => Promise<void>;
  resend: (invitationId: string) => Promise<void>;
  clearError: () => void;
}

/**
 * Pending platform invitations — the accounts a superadmin has invited that do
 * not exist yet (§4.3, §6.1).
 *
 * Every action reloads the list rather than patching it: a resend resets the
 * expiry the row shows, and an invitation that lands after the last load is
 * exactly what the operator wants to see.
 */
export function usePlatformInvitations(): UsePlatformInvitationsResult {
  const [data, setData] = useState<InvitationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      setData(await adminApi.listPlatformInvitations());
      setError(null);
    } catch (loadError) {
      setError(describeAdminError(loadError, 'Failed to load invitations'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const invite = useCallback(
    async (email: string) => {
      setError(null);
      try {
        await adminApi.createPlatformInvitation(email);
      } catch (inviteError) {
        setError(describeAdminError(inviteError, 'Failed to send the invitation'));
        throw inviteError;
      } finally {
        // Even a 502 `email_failed` leaves the invitation behind — only
        // delivery failed — so the list must show it, with its Resend button.
        await refresh();
      }
    },
    [refresh]
  );

  const revoke = useCallback(
    async (invitationId: string) => {
      setError(null);
      try {
        await adminApi.revokePlatformInvitation(invitationId);
      } catch (revokeError) {
        setError(describeAdminError(revokeError, 'Failed to revoke the invitation'));
        throw revokeError;
      }
      await refresh();
    },
    [refresh]
  );

  const resend = useCallback(
    async (invitationId: string) => {
      setError(null);
      try {
        await adminApi.resendPlatformInvitation(invitationId);
      } catch (resendError) {
        setError(describeAdminError(resendError, 'Failed to resend the invitation'));
        throw resendError;
      }
      await refresh();
    },
    [refresh]
  );

  const clearError = useCallback(() => setError(null), []);

  return { data, isLoading, error, refresh, invite, revoke, resend, clearError };
}
